import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Briefcase, MapPin, ChevronRight, ChevronDown, ChevronUp, User } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const ACTIVE = ['cantiere_avviato', 'in_corso'];
const PAGE = 4;

const alertStyle = {
  sospeso: 'bg-red-100 text-red-700',
  da_avviare: 'bg-blue-100 text-blue-700',
  attesa_scelte: 'bg-amber-100 text-amber-700',
  attesa_materiali: 'bg-orange-100 text-orange-700',
};

const salColor = (p) => (p >= 75 ? 'bg-emerald-500' : p >= 40 ? 'bg-[#FF7819]' : 'bg-blue-400');

const SummaryMetric = ({ kpi }) => (
  <Link to={kpi.link || '/projects'} data-testid={`pm-commesse-metric-${kpi.id}`}
    className="flex-1 min-w-[110px] px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors no-underline text-inherit">
    <p className="text-xs text-gray-500 truncate">{kpi.title}</p>
    <p className="text-lg font-bold text-[#3C3C3C] leading-tight" data-testid={`pm-commesse-metric-${kpi.id}-value`}>
      {kpi.display != null ? kpi.display : (kpi.count ?? 0)}
    </p>
  </Link>
);

const ProjectCard = ({ p, hideAmount }) => (
  <Link to={p.link || `/projects/${p.id}`} data-testid={`pm-project-${p.id}`}
    className="block p-3 rounded-xl border border-[#EEE] hover:border-[#FF7819]/40 hover:shadow-sm transition-all group no-underline text-inherit">
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold text-gray-900 truncate">{p.client_name}</p>
      {p.alert_label ? (
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${alertStyle[p.alert] || 'bg-gray-100 text-gray-600'}`} data-testid={`pm-project-alert-${p.id}`}>
          {p.alert_label}
        </span>
      ) : (
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">{p.status_label}</span>
      )}
    </div>
    {p.site_address ? (
      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1 truncate">
        <MapPin className="w-3 h-3 shrink-0" /> {p.site_address}
      </p>
    ) : null}
    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1 truncate">
      <User className="w-3 h-3 shrink-0" />
      {p.is_direct ? <span className="text-[#008A3D] font-medium">Gestione diretta</span> : (p.technician_name || 'Tecnico non assegnato')}
    </p>
    <div className="mt-2 flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${salColor(p.sal_percentage)}`} style={{ width: `${Math.min(100, Math.max(0, p.sal_percentage))}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">SAL {p.sal_percentage}%</span>
    </div>
    {p.next_milestone_name ? (
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-xs text-gray-500 truncate">Prossimo: {p.next_milestone_name}</span>
        {p.next_milestone_amount && !hideAmount ? <span className="text-xs font-semibold text-[#3C3C3C] whitespace-nowrap">{formatCurrency(p.next_milestone_amount)}</span> : null}
      </div>
    ) : null}
  </Link>
);

export const CommesseBlock = ({ summary = [], projects = [], technicians = [], hideMilestoneAmount = false, title = 'Portafoglio commesse' }) => {
  const [stateFilter, setStateFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [visible, setVisible] = useState(PAGE);

  const counts = useMemo(() => {
    const base = techFilter === 'all' ? projects : projects.filter((p) => p.technician_id === techFilter);
    return {
      attivi: base.filter((p) => ACTIVE.includes(p.status)).length,
      da_avviare: base.filter((p) => p.alert === 'da_avviare').length,
      sospesi: base.filter((p) => p.status === 'sospeso').length,
    };
  }, [projects, techFilter]);

  const filtered = useMemo(() => projects.filter((p) => {
    const sOk = stateFilter === 'all'
      || (stateFilter === 'attivi' && ACTIVE.includes(p.status))
      || (stateFilter === 'da_avviare' && p.alert === 'da_avviare')
      || (stateFilter === 'sospesi' && p.status === 'sospeso')
      || (stateFilter === 'attenzione' && !!p.alert);
    const tOk = techFilter === 'all' || p.technician_id === techFilter;
    return sOk && tOk;
  }), [projects, stateFilter, techFilter]);

  useEffect(() => { setVisible(PAGE); }, [stateFilter, techFilter]);

  const chips = [
    { key: 'all', label: 'Tutti' },
    { key: 'attivi', label: 'Attivi', n: counts.attivi },
    { key: 'da_avviare', label: 'Da avviare', n: counts.da_avviare },
    { key: 'sospesi', label: 'Sospesi', n: counts.sospesi },
    { key: 'attenzione', label: 'Attenzione' },
  ];

  const shown = filtered.slice(0, visible);

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm" data-testid="pm-commesse">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#FF7819]" />
          {title}
        </CardTitle>
        <Link to="/projects" className="text-sm text-[#FF7819] hover:underline flex items-center gap-1" data-testid="pm-projects-all-link">
          Vedi tutte <ChevronRight className="w-4 h-4" />
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-2 pb-3 mb-3 border-b border-gray-100" data-testid="pm-commesse-summary">
          {summary.map((kpi) => <SummaryMetric key={kpi.id} kpi={kpi} />)}
        </div>

        {/* Filtri */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="pm-commesse-filters">
          {chips.map((c) => (
            <button key={c.key} onClick={() => setStateFilter(c.key)} data-testid={`pm-filter-${c.key}`}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${stateFilter === c.key ? 'bg-[#FF7819] text-white border-[#FF7819]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF7819]/40'}`}>
              {c.label}{c.n != null ? ` ${c.n}` : ''}
            </button>
          ))}
          {technicians.length > 0 && (
            <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="pm-filter-tecnico"
              className="text-xs px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-600 ml-auto focus:outline-none focus:border-[#FF7819]">
              <option value="all">Tecnico: Tutti</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            {projects.length === 0 ? 'Nessuna commessa assegnata' : 'Nessuna commessa in questo stato'}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="pm-commesse-grid">
              {shown.map((p) => <ProjectCard key={p.id} p={p} hideAmount={hideMilestoneAmount} />)}
            </div>
            {filtered.length > PAGE && (
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500" data-testid="pm-commesse-count">{Math.min(visible, filtered.length)} di {filtered.length}</span>
                <div className="flex items-center gap-3">
                  {visible < filtered.length && (
                    <button onClick={() => setVisible((v) => v + PAGE)} data-testid="pm-commesse-more"
                      className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
                      Mostra altre {Math.min(PAGE, filtered.length - visible)} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {visible > PAGE && (
                    <button onClick={() => setVisible(PAGE)} data-testid="pm-commesse-less"
                      className="text-xs font-medium text-gray-500 hover:underline flex items-center gap-1">
                      Riduci <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
